import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  ClipboardList,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  History,
  Users,
  Bell,
  Settings,
  LogOut
} from "lucide-react";
import useNotifications from "../hooks/useNotifications";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmDialog from "./ConfirmDialog";

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const { unread } = useNotifications();

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    setUser(storedUser);
  }, []);

  const menu = [
    { label: "Dashboard", icon: <LayoutDashboard size={18} />, path: "/dashboard" },
    { label: "Submitted ESMP's", icon: <ClipboardList size={18} />, path: "/submitted-esmp" },
    { label: "Pending Reviews", icon: <Clock size={18} />, path: "/admin/pending" },
    { label: "Overdue ESMPs", icon: <AlertTriangle size={18} />, path: "/admin/overdue" },
    { label: "Returned ESMP's", icon: <CheckCircle size={18} />, path: "/admin/returned" },
    { label: "Rejected ESMP's", icon: <XCircle size={18} />, path: "/admin/rejected" },
    { label: "Approved ESMP's", icon: <CheckCircle size={18} />, path: "/admin/approved" },
    { label: "Manage Roles", icon: <Users size={18} />, path: "/manage-roles" },
    { label: "Statistics & Reports", icon: <ClipboardList size={18} />, path: "/statistics" },
    { label: "Audit Logs", icon: <History size={18} />, path: "/audit-logs" },
    { label: "Notifications", icon: <Bell size={18} />, path: "/notifications" },
    { label: "Settings", icon: <Settings size={18} />, path: "/settings" },
  ];

  const handleLogout = () => {
    setShowLogoutDialog(true);
  };

  const confirmLogout = () => {
    setShowLogoutDialog(false);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    navigate("/");
  };

  return (
    <aside className="w-72 bg-white min-h-screen shadow-lg flex flex-col">
      {/* Brand Header */}
      <div className="px-5 py-6 bg-gradient-to-r from-green-800 to-green-600 text-white">
        <div className="flex items-center gap-3">
          <div className="bg-white text-green-700 rounded-full w-10 h-10 flex items-center justify-center font-bold">
            {user?.name ? user.name[0].toUpperCase() : "U"}
          </div>
          <div>
            <h3 className="text-sm font-semibold">{user?.name || "User"}</h3>
            <p className="text-xs text-green-200">{user?.role || "Role"}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="px-4 py-4 flex-1">
        <ul className="space-y-2 text-sm">
          {menu.map((item, i) => {
            const isActive = location.pathname === item.path;
              return (
              <li
                key={i}
                onClick={() => navigate(item.path)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg cursor-pointer transition-all ${
                  isActive
                    ? "bg-green-100 text-green-700 font-semibold border-l-4 border-green-600"
                    : "text-gray-700 hover:bg-green-50 hover:text-green-700"
                }`}
              >
                <span className="flex items-center">{item.icon}</span>
                  <span className="flex items-center justify-between w-full">
                    <span>{item.label}</span>
                    {item.path === '/notifications' && unread > 0 && (
                      <span className="ml-2 bg-red-500 text-white rounded-full px-2 text-xs font-semibold">
                        {unread}
                      </span>
                    )}
                  </span>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="border-t px-5 py-4 space-y-3">
        <button
          className="text-sm text-gray-600 hover:text-green-700 transition"
          onClick={() => alert("Help is coming soon!")}
        >
          Help
        </button>

        <button
          className="flex items-center gap-2 text-sm text-red-500 hover:text-red-600 transition"
          onClick={handleLogout}
        >
          <LogOut size={18} />
          Log out
        </button>
      </div>

      <ConfirmDialog
        open={showLogoutDialog}
        title="Confirm Logout"
        message="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutDialog(false)}
      />
    </aside>
  );
}
